import { useOrganizationUser } from "src/api/taktile/queries";
import { Pill } from "src/base-components/Pill";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { OrganizationUser } from "src/clients/taktile-api";
import { UserNotFoundPill } from "src/entities/Assignees";
import { DashboardPageParams } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";
import { getUserName } from "src/utils/user";

type UserByIdProps = {
  userId: string;
  user?: never;
};

type UserByDataProps = {
  user: OrganizationUser;
  userId?: never;
};

type CommonProps = {
  postfixIcon?: React.ReactElement<typeof Pill.Icon>;
  fullWidth?: boolean;
};

type UserPillProps = CommonProps & (UserByIdProps | UserByDataProps);

/**
 * UserPill component displays a user in a pill container.
 *
 * This component can be used in two ways:
 * 1. By providing a userId - it will fetch the user data from the API
 * 2. By providing the user data directly - for cases where the data is already available
 *
 * Features:
 * - Shows user avatar (either from URL or generated with BoringAvatar)
 * - Displays user name
 * - Supports loading state with skeleton placeholder
 * - Handles user not found state
 * - Optional postfix icon and full width styling
 */
export const UserPill: React.FC<UserPillProps> = (props) => {
  const { postfixIcon, fullWidth = false } = props;

  const { orgId } = useParamsDecode<DashboardPageParams>();
  const { data: loadedUser, isLoading } = useOrganizationUser(
    orgId,
    props.userId,
  );

  const user = props.user ? props.user : loadedUser;

  if (isLoading) {
    return <SkeletonPlaceholder height="h-5" width="w-full" />;
  }

  if (!user && props.userId) {
    return <UserNotFoundPill id={props.userId} />;
  }

  if (!user) {
    return null;
  }

  return (
    <UserPillWithUser
      fullWidth={fullWidth}
      postfixIcon={postfixIcon}
      user={user}
    />
  );
};

const UserPillWithUser: React.FC<UserByDataProps & CommonProps> = ({
  user,
  postfixIcon,
  fullWidth = false,
}) => {
  return (
    <Pill fullWidth={fullWidth} size="sm" variant="gray">
      <Pill.Avatar user={user} />
      <Pill.Text>{getUserName(user)}</Pill.Text>
      {postfixIcon}
    </Pill>
  );
};
